import React, { useState, useEffect, useContext, useMemo } from "react";
import { DataContext } from "../contexts/DataContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faSync,
  faFileUpload,
  faChartLine,
  faExclamationTriangle,
  faCheckCircle,
  faHourglass,
  faClock,
  faShip,
  faTruck,
  faPlane,
  faWarehouse,
  faArrowRight,
  faFilter,
  faInfoCircle,
  faCalendarAlt,
  faEye,
  faChevronDown,
  faUser,
  faSignOutAlt,
  faQuestionCircle,
  faBell,
  faCog,
  faEnvelope,
  faClipboardList,
  faTruckLoading,
  faShippingFast,
  faFileAlt,
  faMoneyCheckAlt,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

const TrafficVisualControlTower = () => {
  const { trafficData, isLoading, fetchTrafficData, setShouldFetchData } =
    useContext(DataContext);
  const [filteredData, setFilteredData] = useState([]);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedReference, setSelectedReference] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [viewMode, setViewMode] = useState("timeline"); // 'timeline' or 'board'

  // Traffic lifecycle milestones with timeline in hours
  const milestones = [
    { name: "Receipt", plannedHours: 0, icon: faWarehouse, color: "blue" },
    {
      name: "Documentation",
      plannedHours: 10,
      icon: faFileUpload,
      color: "yellow",
    },
    {
      name: "Load Order",
      plannedHours: 20,
      icon: faClipboardList,
      color: "purple",
    },
    {
      name: "Loading",
      plannedHours: 30,
      icon: faTruckLoading,
      color: "indigo",
    },
    {
      name: "Yard Outbound",
      plannedHours: 36,
      icon: faShippingFast,
      color: "green",
    },
    { name: "DODA", plannedHours: 45, icon: faFileAlt, color: "orange" },
    { name: "Crossing", plannedHours: 50, icon: faArrowRight, color: "red" },
    {
      name: "Payment",
      plannedHours: 60,
      icon: faMoneyCheckAlt,
      color: "emerald",
    },
    { name: "Completed", plannedHours: 72, icon: faCheckCircle, color: "teal" },
  ];

  // Trigger data fetch when component mounts
  useEffect(() => {
    setShouldFetchData((prev) => ({ ...prev, fetchTrafficData: true }));

    return () => {
      setShouldFetchData((prev) => ({ ...prev, fetchTrafficData: false }));
    };
  }, [setShouldFetchData]);

  useEffect(() => {
    fetchTrafficData();
  }, [fetchTrafficData]);

  // Helper to check if a shipment is closed/completed
  const isShipmentClosed = (item) => {
    // Safety check
    if (!item || !item.header) return false;

    // Check trafficStatus field for completion indicators
    const trafficStatus = item.header.trafficStatus;
    if (trafficStatus) {
      const closedStatuses = ['pagado', 'delivered', 'entregado', 'completed', 'cerrado'];
      if (closedStatuses.includes(trafficStatus.toLowerCase())) {
        return true;
      }
    }

    // Check detail.traffic_data.status field
    const detailStatus = item.header.detail?.traffic_data?.status;
    if (detailStatus) {
      const closedStatuses = ['pagado', 'delivered', 'entregado', 'completed', 'cerrado'];
      if (closedStatuses.includes(detailStatus.toLowerCase())) {
        return true;
      }
    }

    // Check if there's a fechaPiso (delivery date)
    if (item.header.ebookingDates && item.header.ebookingDates.fechaPiso) {
      return true;
    }

    // Check if pedimento has been paid (date_pedimento_paid exists)
    if (item.header.detail?.dates_data?.date_pedimento_paid) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (trafficData) {
      // Filter out closed/completed references
      const openReferences = trafficData.filter(
        (item) => !isShipmentClosed(item)
      );
      setFilteredData(openReferences);
    }
  }, [trafficData]);

  // Calculate reference status based on timeline
  const calculateReferenceStatus = (reference) => {
    // Check if shipment is closed/completed first
    if (isShipmentClosed(reference)) {
      return { status: "completed", color: "emerald" };
    }

    const dates = reference.header.detail?.dates_data || {};
    const dateInit = dates.date_init ? new Date(dates.date_init) : new Date();
    const now = new Date();
    const hoursElapsed = (now - dateInit) / (1000 * 60 * 60);

    // Find current milestone based on available dates
    let currentMilestone = 0;
    if (dates.date_inbound) currentMilestone = 1;
    if (dates.date_loading_order) currentMilestone = 2;
    if (dates.date_init_loading) currentMilestone = 3;
    if (dates.date_yard_outbound) currentMilestone = 4;
    if (dates.date_doda) currentMilestone = 5;
    if (dates.date_crossing) currentMilestone = 6;
    if (dates.date_pedimento_paid) currentMilestone = 7;

    const expectedHours = milestones[currentMilestone]?.plannedHours || 0;
    const delayHours = hoursElapsed - expectedHours;

    if (delayHours > 12) return { status: "delayed", color: "red" };
    if (delayHours > 6) return { status: "at-risk", color: "yellow" };
    return { status: "on-time", color: "green" };
  };

  // Get transport type icon
  const getTransportIcon = (entryPoint) => {
    const transportMap = {
      160: { icon: faShip, color: "text-blue-600" },
      510: { icon: faShip, color: "text-blue-600" },
      430: { icon: faShip, color: "text-blue-600" },
      240: { icon: faTruck, color: "text-green-600" },
      520: { icon: faPlane, color: "text-purple-600" },
      521: { icon: faPlane, color: "text-purple-600" },
      640: { icon: faPlane, color: "text-purple-600" },
    };
    return (
      transportMap[entryPoint] || { icon: faTruck, color: "text-gray-600" }
    );
  };

  // Timeline view component
  const TimelineView = () => {
    const currentTime = new Date();
    const timelineWidth = 1200; // pixels
    const timelineHours = 72; // total hours to display

    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold mb-6">
          Active References Timeline
        </h3>

        <div className="relative overflow-x-auto">
          {/* Timeline header with hours */}
          <div
            className="flex mb-4 relative"
            style={{ width: `${timelineWidth}px` }}
          >
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex-1 text-center text-sm text-gray-600">
                {i * 12}h
              </div>
            ))}
          </div>

          {/* Current time indicator */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
            style={{
              left: `${
                (currentTime.getHours() / timelineHours) * timelineWidth
              }px`,
            }}
          >
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-2 py-1 rounded text-xs">
              Now
            </div>
          </div>

          {/* Milestone markers */}
          <div
            className="relative h-8 mb-4"
            style={{ width: `${timelineWidth}px` }}
          >
            {milestones.map((milestone, i) => (
              <div
                key={i}
                className="absolute flex flex-col items-center"
                style={{
                  left: `${
                    (milestone.plannedHours / timelineHours) * timelineWidth
                  }px`,
                }}
              >
                <div
                  className={`w-3 h-3 rounded-full bg-${milestone.color}-500`}
                />
                <span className="text-xs mt-1 whitespace-nowrap">
                  {milestone.name}
                </span>
              </div>
            ))}
          </div>

          {/* References on timeline */}
          <div className="space-y-3">
            {filteredData.map((reference, index) => {
              const dates = reference.header.detail?.dates_data || {};
              const dateInit = dates.date_init
                ? new Date(dates.date_init)
                : currentTime;
              const hoursElapsed = (currentTime - dateInit) / (1000 * 60 * 60);
              const status = calculateReferenceStatus(reference);
              const transport = getTransportIcon(
                reference.header.pedimento.pedimento_entry_point
              );

              return (
                <div key={index} className="relative h-12 flex items-center">
                  {/* Reference info */}
                  <div className="w-48 pr-4 flex items-center space-x-2">
                    <FontAwesomeIcon
                      icon={transport.icon}
                      className={transport.color}
                    />
                    <span className="font-medium">
                      {reference.header.reference}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${status.color}-100 text-${status.color}-800`}
                    >
                      {status.status}
                    </span>
                  </div>

                  {/* Timeline bar */}
                  <div
                    className="flex-1 relative h-8"
                    style={{ width: `${timelineWidth}px` }}
                  >
                    <div
                      className="absolute inset-y-0 left-0 bg-gray-200 rounded"
                      style={{ width: `${timelineWidth}px` }}
                    >
                      <div
                        className={`absolute inset-y-0 left-0 bg-${status.color}-500 rounded`}
                        style={{
                          width: `${Math.min(
                            (hoursElapsed / timelineHours) * timelineWidth,
                            timelineWidth
                          )}px`,
                        }}
                      />
                    </div>

                    {/* Show completed milestones */}
                    {milestones.map((milestone, i) => {
                      const milestoneDate =
                        dates[`date_${milestone.name.toLowerCase()}`];
                      if (milestoneDate) {
                        const milestoneTime = new Date(milestoneDate);
                        const milestoneHours =
                          (milestoneTime - dateInit) / (1000 * 60 * 60);
                        return (
                          <div
                            key={i}
                            className="absolute w-4 h-4 rounded-full bg-white border-2 border-blue-500"
                            style={{
                              left: `${
                                (milestoneHours / timelineHours) * timelineWidth
                              }px`,
                              top: "50%",
                              transform: "translate(-50%, -50%)",
                            }}
                          />
                        );
                      }
                      return null;
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Board view component
  const BoardView = () => {
    const columns = [
      { title: "On Time", status: "on-time", color: "green" },
      { title: "At Risk", status: "at-risk", color: "yellow" },
      { title: "Delayed", status: "delayed", color: "red" },
      { title: "Completed", status: "completed", color: "emerald" },
    ];

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {columns.map((column) => {
          const columnData = filteredData.filter(
            (ref) => calculateReferenceStatus(ref).status === column.status
          );

          return (
            <div
              key={column.status}
              className="bg-white rounded-lg shadow-lg p-4"
            >
              <div
                className={`flex items-center justify-between mb-4 pb-2 border-b-2 border-${column.color}-500`}
              >
                <h3 className="text-lg font-semibold">{column.title}</h3>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium bg-${column.color}-100 text-${column.color}-800`}
                >
                  {columnData.length}
                </span>
              </div>

              <div className="space-y-3">
                {columnData.map((reference, index) => {
                  const transport = getTransportIcon(
                    reference.header.pedimento.pedimento_entry_point
                  );

                  return (
                    <div
                      key={index}
                      className="p-3 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => setSelectedReference(reference)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <FontAwesomeIcon
                            icon={transport.icon}
                            className={transport.color}
                          />
                          <span className="font-medium">
                            {reference.header.reference}
                          </span>
                        </div>
                        <FontAwesomeIcon
                          icon={faEye}
                          className="text-gray-400 hover:text-blue-600"
                        />
                      </div>

                      <div className="text-sm text-gray-600">
                        <div>Customer: {reference.header.customer_name}</div>
                        <div>
                          Started:{" "}
                          {reference.header.detail?.dates_data?.date_init
                            ? new Date(
                                reference.header.detail.dates_data.date_init
                              ).toLocaleDateString()
                            : "N/A"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Summary statistics
  const summaryStats = useMemo(() => {
    const stats = {
      total: filteredData.length,
      onTime: 0,
      atRisk: 0,
      delayed: 0,
    };

    filteredData.forEach((ref) => {
      const status = calculateReferenceStatus(ref).status;
      if (status === "on-time") stats.onTime++;
      if (status === "at-risk") stats.atRisk++;
      if (status === "delayed") stats.delayed++;
    });

    return stats;
  }, [filteredData]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setFileUploading(true);
    setUploadProgress(0);

    try {
      // Simulate upload - replace with actual API call
      const interval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            setFileUploading(false);
            fetchTrafficData();
            return 100;
          }
          return prev + 10;
        });
      }, 300);
    } catch (err) {
      console.error("Error uploading file:", err);
      setFileUploading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <FontAwesomeIcon
          icon={faSpinner}
          className="text-blue-500 text-4xl animate-spin"
        />
        <span className="ml-3 text-xl">Loading control tower data...</span>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="w-full bg-white shadow-lg">
        <div className="bg-gradient-to-r from-[#003366] to-blue-700 text-white">
          {/* Corporate Menu Bar */}
          <div className="flex justify-end items-center px-6 py-2">
            <div className="flex items-center space-x-4 text-sm">
              <button className="text-blue-100 hover:text-white flex items-center">
                <FontAwesomeIcon icon={faQuestionCircle} className="mr-1" />
                <span>Help</span>
              </button>

              {/* Notifications dropdown */}
              <div className="relative dropdown-container">
                <button
                  className="text-blue-100 hover:text-white flex items-center"
                  onClick={() => {
                    setShowNotifications(!showNotifications);
                    setShowUserMenu(false);
                  }}
                >
                  <div className="relative">
                    <FontAwesomeIcon icon={faBell} />
                  </div>
                  <span className="ml-1">Notifications</span>
                </button>

                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg overflow-hidden z-20 text-gray-800">
                    <div className="py-2 px-3 bg-gray-100 border-b border-gray-200 flex justify-between items-center">
                      <span className="font-medium">Notifications</span>
                      <span className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">
                        Mark all as read
                      </span>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      <div className="px-4 py-6 text-center text-gray-500">
                        No new notifications
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Messages */}
              <button className="text-blue-100 hover:text-white flex items-center">
                <FontAwesomeIcon icon={faEnvelope} />
                <span className="ml-1">Messages</span>
              </button>

              {/* Settings */}
              <button className="text-blue-100 hover:text-white flex items-center">
                <FontAwesomeIcon icon={faCog} />
                <span className="ml-1">Settings</span>
              </button>

              {/* User menu dropdown */}
              <div className="relative ml-2 dropdown-container">
                <button
                  className="flex items-center space-x-2 bg-blue-800 hover:bg-blue-900 px-3 py-1 rounded text-sm transition-colors duration-200"
                  onClick={() => {
                    setShowUserMenu(!showUserMenu);
                    setShowNotifications(false);
                  }}
                >
                  <div className="h-6 w-6 rounded-full bg-blue-200 flex items-center justify-center text-blue-800">
                    <FontAwesomeIcon icon={faUser} size="xs" />
                  </div>
                  <span>Control Tower Admin</span>
                  <FontAwesomeIcon icon={faChevronDown} size="xs" />
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg overflow-hidden z-20 text-gray-800">
                    <div className="py-3 px-4 border-b border-gray-100">
                      <p className="text-sm font-medium">Control Tower Admin</p>
                      <p className="text-xs text-gray-500">admin@company.com</p>
                    </div>
                    <div>
                      <a
                        href="#"
                        className="block px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        <FontAwesomeIcon
                          icon={faUser}
                          className="mr-2 text-gray-500"
                        />
                        My Profile
                      </a>
                      <a
                        href="#"
                        className="block px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        <FontAwesomeIcon
                          icon={faCog}
                          className="mr-2 text-gray-500"
                        />
                        Account Settings
                      </a>
                      <a
                        href="#"
                        className="block px-4 py-2 text-sm border-t border-gray-100 hover:bg-gray-100 text-red-600"
                      >
                        <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
                        Sign Out
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main header with title and action buttons */}
          <div className="flex justify-between items-center px-6 pt-5 pb-7">
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon icon={faChartLine} className="text-2xl" />
              <h1 className="text-2xl font-bold tracking-tight">
                Traffic Visual Control Tower
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <input
                  type="file"
                  id="fileUpload"
                  className="absolute inset-0 opacity-0 w-full cursor-pointer"
                  onChange={handleFileUpload}
                  accept=".xlsx,.xls"
                  disabled={fileUploading}
                />
                <button
                  className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                  disabled={fileUploading}
                >
                  <FontAwesomeIcon
                    icon={fileUploading ? faSpinner : faFileUpload}
                    className={fileUploading ? "animate-spin" : ""}
                  />
                  <span>
                    {fileUploading
                      ? `Uploading (${uploadProgress}%)`
                      : "Import Data"}
                  </span>
                </button>
              </div>

              <button
                onClick={fetchTrafficData}
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                disabled={isLoading}
              >
                <FontAwesomeIcon
                  icon={isLoading ? faSpinner : faSync}
                  className={isLoading ? "animate-spin" : ""}
                />
                <span>{isLoading ? "Refreshing..." : "Refresh Data"}</span>
              </button>
            </div>
          </div>
        </div>

        {/* View mode toggle */}
        <div className="bg-white border-b px-6 py-3 flex items-center justify-between">
          <div className="flex space-x-4">
            <button
              onClick={() => setViewMode("timeline")}
              className={`px-4 py-2 rounded-lg font-medium ${
                viewMode === "timeline"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Timeline View
            </button>
            <button
              onClick={() => setViewMode("board")}
              className={`px-4 py-2 rounded-lg font-medium ${
                viewMode === "board"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Board View
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={faFilter} className="text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm"
            >
              <option value="all">All References</option>
              <option value="on-time">On Time</option>
              <option value="at-risk">At Risk</option>
              <option value="delayed">Delayed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="p-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Active
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {summaryStats.total}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <FontAwesomeIcon
                  icon={faShippingFast}
                  className="text-blue-600 text-xl"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">On Time</p>
                <p className="text-2xl font-bold text-green-600">
                  {summaryStats.onTime}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="text-green-600 text-xl"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">At Risk</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {summaryStats.atRisk}
                </p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-yellow-600 text-xl"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Delayed</p>
                <p className="text-2xl font-bold text-red-600">
                  {summaryStats.delayed}
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <FontAwesomeIcon
                  icon={faClock}
                  className="text-red-600 text-xl"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main visualization */}
        {viewMode === "timeline" ? <TimelineView /> : <BoardView />}
      </div>

      {/* Reference details modal */}
      {selectedReference && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Reference Details</h3>
              <button
                onClick={() => setSelectedReference(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Reference</p>
                  <p className="font-medium">
                    {selectedReference.header.reference}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Customer</p>
                  <p className="font-medium">
                    {selectedReference.header.customer_name}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <p className="font-medium">
                    {selectedReference.header.detail?.traffic_data?.status ||
                      "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Entry Point</p>
                  <p className="font-medium">
                    {selectedReference.header.pedimento.pedimento_entry_point}
                  </p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Timeline Progress</h4>
                <div className="space-y-2">
                  {milestones.map((milestone, index) => {
                    const dateField = `date_${milestone.name
                      .toLowerCase()
                      .replace(" ", "_")}`;
                    const date =
                      selectedReference.header.detail?.dates_data?.[dateField];

                    return (
                      <div key={index} className="flex items-center">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            date
                              ? "bg-green-100 text-green-600"
                              : "bg-gray-100 text-gray-400"
                          }`}
                        >
                          <FontAwesomeIcon icon={milestone.icon} />
                        </div>
                        <div className="ml-3">
                          <p className="font-medium">{milestone.name}</p>
                          <p className="text-sm text-gray-600">
                            {date ? new Date(date).toLocaleString() : "Pending"}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrafficVisualControlTower;
